import './style.scss';
import { withNamespaces } from 'react-i18next';
import { Button, Input, Label } from 'reactstrap';
import { INCIDENT_DEADLINES } from './constants';
import {
    createEmptyCategoryOption,
    isPresetReportingDeadline,
    updateCategoryOptionField,
    updateCategoryOptionFields
} from './helpers';

/**
 * GDPR Category question Designer extension.
 *
 * Built for the shared Questionnaire Designer `customQuestionTypes` API
 * (`DesignerCustomQuestionTypeProps`). Registered from the GDPR questionnaire
 * edit page via `categoryCustomQuestionType`.
 *
 * Category options are always checkboxes — there is no per-option type selector.
 *
 * Props (from shared Designer):
 * - question
 * - questionId
 * - subQuestion
 * - setElementField
 * - setElementStateField
 * - addQuestionOption
 * - deleteQuestionOption
 * - setQuestionOptionField
 * - setQuestionOptionStateField
 * - updateOptions
 * - reorder
 */
const CategoryQuestionDesigner = ({
    t,
    lng,
    question,
    questionId,
    addQuestionOption,
    deleteQuestionOption,
    setQuestionOptionField,
    updateOptions
}) => {
    const options = Array.isArray(question?.options) ? question.options : [];
    const isEnglish = lng === 'en';

    /**
     * Prefer the granular shared API so unrelated option fields are preserved
     * by the Designer's spread-based updater. Fall back to updateOptions.
     */
    const handleOptionFieldChange = (optionIndex, field, value) => {
        if (typeof setQuestionOptionField === 'function') {
            setQuestionOptionField(questionId, optionIndex, field, value);
            return;
        }

        if (typeof updateOptions === 'function') {
            updateOptions(
                updateCategoryOptionField(options, optionIndex, field, value)
            );
        }
    };

    /**
     * Multi-field option patch in one write so sibling properties stay intact
     * and React state updates do not race.
     */
    const handleOptionFieldsChange = (optionIndex, fields) => {
        if (typeof updateOptions === 'function') {
            updateOptions(
                updateCategoryOptionFields(options, optionIndex, fields)
            );
            return;
        }

        Object.keys(fields || {}).forEach((field) => {
            handleOptionFieldChange(optionIndex, field, fields[field]);
        });
    };

    const handleTitleChange = (optionIndex, value) => {
        handleOptionFieldChange(optionIndex, 'title', value);
    };

    const handleReportingDeadlineChange = (optionIndex, value) => {
        // Selecting a preset clears any temporary custom-hours UI field.
        // Selecting "custom" mirrors the existing GDPR designer marker.
        if (value === 'custom') {
            handleOptionFieldChange(optionIndex, 'reportingDeadlineHours', 'custom');
            return;
        }

        const option = options[optionIndex] || {};
        const next = {
            ...option,
            reportingDeadlineHours: value
        };
        delete next.customReportingDeadlineHours;

        if (typeof updateOptions === 'function') {
            updateOptions(
                options.map((item, index) => (index === optionIndex ? next : item))
            );
            return;
        }

        handleOptionFieldChange(optionIndex, 'reportingDeadlineHours', value);
    };

    const handleCustomReportingDeadlineChange = (optionIndex, value) => {
        // Keep reportingDeadlineHours as 'custom' while editing the hours input,
        // matching the existing GDPR designer save prep.
        handleOptionFieldsChange(optionIndex, {
            reportingDeadlineHours: 'custom',
            customReportingDeadlineHours: value
        });
    };

    const handleAddOption = () => {
        if (!isEnglish || typeof addQuestionOption !== 'function') {
            return;
        }

        addQuestionOption(questionId, createEmptyCategoryOption());
    };

    const handleDeleteOption = (optionIndex) => {
        if (!isEnglish || typeof deleteQuestionOption !== 'function') {
            return;
        }

        deleteQuestionOption(questionId, optionIndex);
    };

    const renderReportingDeadline = (option, optionIndex) => {
        const optionCurrentValue = option.reportingDeadlineHours;
        const isPreset = isPresetReportingDeadline(optionCurrentValue, INCIDENT_DEADLINES);
        const selectValue = !isPreset && optionCurrentValue != null && optionCurrentValue !== ''
            ? 'custom'
            : (optionCurrentValue ?? '');
        const customValue = option.customReportingDeadlineHours != null
            ? option.customReportingDeadlineHours
            : (!isPreset ? optionCurrentValue : '');

        return (
            <div className="gdpr-category-question-designer__deadline">
                <div className="gdpr-category-question-designer__deadline-select-wrap">
                    <Input
                        type="select"
                        value={selectValue === '' || selectValue == null ? '' : selectValue}
                        disabled={!isEnglish}
                        className="dissco--question-genrator--form-control dissco--question-genrator--input"
                        onChange={(e) => handleReportingDeadlineChange(optionIndex, e.target.value === 'custom'
                            ? 'custom'
                            : (e.target.value === '' ? undefined : Number(e.target.value) || e.target.value)
                        )}
                    >
                        <option disabled value="">{t('Select Deadline')}</option>
                        {INCIDENT_DEADLINES.map((deadline, deadlineIndex) => (
                            <option key={deadlineIndex} value={deadline.value}>
                                {t(deadline.label)}
                            </option>
                        ))}
                    </Input>
                    <i className="ri-arrow-down-s-line gdpr-category-question-designer__select-icon" />
                </div>

                {(selectValue === 'custom') && (
                    <div className="gdpr-category-question-designer__deadline-custom">
                        <Input
                            type="number"
                            min="1"
                            disabled={!isEnglish}
                            placeholder={t('Enter Custom Deadline')}
                            value={customValue ?? ''}
                            onChange={(e) => handleCustomReportingDeadlineChange(optionIndex, e.target.value)}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="gdpr-category-question-designer">
            {options.length > 0 && (
                <div className="gdpr-category-question-designer__options">
                    <Label className="form-label gdpr-category-question-designer__options-label">
                        {t('Options')}
                    </Label>

                    {options.map((option, optionIndex) => (
                        <div
                            key={option.id != null ? String(option.id) : `option-${optionIndex}`}
                            className="gdpr-category-question-designer__option"
                        >
                            <div className="gdpr-category-question-designer__option-label">
                                <span>{`${t('Option')} ${optionIndex + 1}`}</span>
                            </div>

                            <div className="gdpr-category-question-designer__option-title">
                                <Input
                                    type="text"
                                    value={t(option.title) || ''}
                                    placeholder={t('Enter title')}
                                    className="dissco--question-genrator--form-control dissco--question-genrator--input"
                                    onChange={(e) => handleTitleChange(optionIndex, e.target.value)}
                                />
                            </div>

                            {renderReportingDeadline(option, optionIndex)}

                            <div className="gdpr-category-question-designer__option-delete">
                                <button
                                    type="button"
                                    title={t('Delete')}
                                    disabled={!isEnglish}
                                    onClick={() => handleDeleteOption(optionIndex)}
                                    className='text-danger'
                                >
                                    <i className="ri-delete-bin-line" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isEnglish && (
                <div className="gdpr-category-question-designer__add">
                    <Button
                        color="secondary"
                        size="md"
                        outline
                        block
                        type="button"
                        onClick={handleAddOption}
                        className="dissco--question-genrator--button dissco--question-genrator--button--md"
                    >
                        {`+ ${t('Add Options')}`}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default withNamespaces()(CategoryQuestionDesigner);
